import { Stack, Typography } from '@mui/material';
import { SxProps, Theme } from '@mui/system';
import { CfSwitch } from '../../../../../cf-ui/Switch/CfSwitch';
import LocationLabel from '../../dialogs/LocationSearchDialog/components/LocationLabel';
import LocationCheckboxList, { LocationCheckboxItem } from '../LocationCheckboxList/LocationCheckboxList';
import LocationSearch, { LocationSearchItem } from './LocationSearch';

export interface LocationSearchCheckboxListProps {
  items: LocationSearchItem[];
  onChange?: (locations: LocationSearchItem[]) => void;
  isLoading?: boolean;
  sx?: SxProps<Theme>;
  checkboxListSx?: SxProps<Theme>;
  greyBackground?: boolean;
  inputDataTestId?: string;
  searchButtonDataTestId?: string;
  selectAllCheckboxDataTestId?: string;
  checkboxDataTestId?: string;
  isAutoFocused?: boolean;
  gap?: number | string | ((theme: Theme) => string | number);
  filterLocationSwitch?: boolean;
  filterLocationSwitchChecked?: boolean;
  filterLocationOnChange?: (checked: boolean) => void;
}

const LocationSearchCheckboxList = (props: LocationSearchCheckboxListProps): JSX.Element => {
  const {
    isLoading,
    checkboxListSx,
    gap,
    greyBackground,
    selectAllCheckboxDataTestId,
    checkboxDataTestId,
    isAutoFocused,
    ...rest
  } = props;

  const handleOnChange = (filteredCheckboxItems: LocationCheckboxItem[]) => {
    const checkboxItemsObj = Object.fromEntries(filteredCheckboxItems.map((i) => [i.customerId, i]));
    const updatedSearchItems = props.items.map((o: LocationSearchItem) => {
      const itemUpdate = checkboxItemsObj[o.customerId];
      return { ...o, selected: itemUpdate ? itemUpdate.selected : o.selected };
    });

    props.onChange?.(updatedSearchItems);
  };

  return (
    <LocationSearch isAutoFocused={isAutoFocused} {...rest}>
      {(filteredItems) => (
        <>
          {props.filterLocationSwitch ? (
            <Stack direction='row' sx={{ alignItems: 'center', width: '100%' }}>
              <CfSwitch isChecked={props.filterLocationSwitchChecked} onChange={props.filterLocationOnChange} />
              <Typography variant='h6'>Show only unassigned</Typography>
            </Stack>
          ) : null}
          <LocationCheckboxList
            items={getInitialItems(filteredItems)}
            onChange={handleOnChange}
            isLoading={isLoading}
            sx={{ ...checkboxListSx }}
            greyBackground={greyBackground}
            dataTestId={selectAllCheckboxDataTestId}
            dataAltTestId={checkboxDataTestId}
            gap={gap}
          />
        </>
      )}
    </LocationSearch>
  );
};

const getInitialItems = (items: LocationSearchItem[]): LocationCheckboxItem[] => {
  return items.map((item: LocationSearchItem) => {
    return {
      customerId: item.customerId,
      selected: item.selected,
      disabled: item.disabled,
      label: <LocationLabel item={item} />,
    } as LocationCheckboxItem;
  });
};

export default LocationSearchCheckboxList;
